import type { ReactElement } from 'react';

import classNames from 'classnames';
import defaults from 'lodash/defaults';
import keys from 'lodash/keys';

import { ScreensBreakpoint, ScreensResponsive } from '@ui/uikit/utils/Screens';
import type { OneOrMany } from '@utilities/helpers/OneOrMany';

import type { Breakpoint} from '../../../../../../styles/exports';
import { BREAKPOINTS } from '../../../../../../styles/exports';
import type { GalleryProps, GallerySlideProps } from '../../Gallery';
import { Gallery, GallerySlide } from '../../Gallery';
import styles from './AdaptiveGallery.module.scss';

interface AdaptiveGalleryProps extends Omit<GalleryProps, 'children'> {
  breakpoints?: Partial<Record<Breakpoint, Partial<GalleryProps>>>;
  children?: OneOrMany<ReactElement<AdaptiveGallerySlideProps, typeof AdaptiveGallerySlide>>;
}

const AdaptiveGallery = ({
  breakpoints = {},
  children = [],
  breakout,
  ...defaultProps
}: AdaptiveGalleryProps) => {
  const slides = Array.isArray(children) ? children : [children];

  return (
    <div className={classNames({ [styles['container--breakout']]: breakout })}>
      <ScreensResponsive>
        {keys(BREAKPOINTS).map((breakpoint) => {
          return (
            <ScreensBreakpoint at={breakpoint as Breakpoint} key={breakpoint}>
              <Gallery {...defaults({}, breakpoints[breakpoint as Breakpoint], defaultProps)}>
                {slides.map(({ props }, index) => {
                  return <GallerySlide {...props} key={index} />;
                })}
              </Gallery>
            </ScreensBreakpoint>
          );
        })}
      </ScreensResponsive>
    </div>
  );
};

type AdaptiveGallerySlideProps = GallerySlideProps;

const AdaptiveGallerySlide = ({ children, ...rest }: AdaptiveGallerySlideProps) => {
  return <GallerySlide {...rest}>{children}</GallerySlide>;
};

AdaptiveGallerySlide.displayName = 'AdaptiveGallery.Slide';

AdaptiveGallery.Slide = AdaptiveGallerySlide;

export { AdaptiveGallery, type AdaptiveGalleryProps };
